import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ButtonBase, Collapse, Paper, Stack, Typography } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';

interface CollapsibleProps {
  title: string;
  children: ReactNode;
  isOpen: boolean;
  toggleOpen: () => void;
  headerActions?: ReactNode;
}

export const Collapsible: React.FC<CollapsibleProps> = ({
  title,
  children,
  isOpen,
  toggleOpen,
  headerActions,
}) => {
  const { t } = useTranslation(NS.adminPages);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        overflow: 'hidden',
        boxShadow: 1,
        transition: 'all 0.2s ease',
      }}
    >
      <ButtonBase
        onClick={toggleOpen}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.75,
          py: 1.25,
          cursor: 'pointer',
          textAlign: 'left',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isOpen ? 'success.main' : 'grey.100',
              color: isOpen ? 'common.white' : 'text.secondary',
              transform: isOpen ? 'rotate(180deg)' : 'none',
              transition: 'all 0.2s ease',
            }}
          >
            <ChevronDown size={16} />
          </Box>
          <Typography variant="body2" fontWeight={600} color="text.primary">
            {title}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          {headerActions && (
            <Box onClick={(e) => e.stopPropagation()}>{headerActions}</Box>
          )}
          <Typography variant="caption" color="text.disabled">
            {isOpen
              ? t(ADMIN_PAGES_KEYS.jobOpenings.ui.clickToCollapse)
              : t(ADMIN_PAGES_KEYS.jobOpenings.ui.clickToExpand)}
          </Typography>
        </Stack>
      </ButtonBase>

      <Collapse in={isOpen}>
        <Box
          sx={{
            p: 2,
            bgcolor: 'action.hover',
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default Collapsible;
